import type { IFieldDefinition } from '@platform/core/types';
import type { IProtoGroup } from './IProtoGroup';
import type { IStandardTop } from './IStandardTop';

/** Порядок стандартних груп прототипу v4 (['Details', ...GSET.groupOrder]): Подробиці, Управління, Зв'язки, Планування, Дати, Нотатки. */
const ORDER = ['Details', 'Management', 'Links', 'Planning', 'Dates', 'Notes'];
const RATED = ['priority', 'severity', 'rank'];
const OWN = ['priority', 'severity', 'assignee', 'rank'];
const NOT_DETAILS = ['user', 'date', 'textarea', 'area', 'iteration', 'ref', 'lookup'];

/**
 * Стандартні групи прототипу v4 (stdGroups) з полів у порядку простору: Управління (виконавець, інші люди, пріоритет, серйозність,
 * відносний пріоритет) · Зв'язки (посилання) · Планування (напрям, ітерація) · Дати · Подробиці (решта; теги — якщо не під заголовком) ·
 * Нотатки (довгі тексти; опис — якщо не під заголовком); назва й статус ключового рядка — поза групами; порожні групи відкидаються,
 * «Подробиці» — першими, «Увесь рядок» — лише в «Нотатках». Ідентифікатор групи — її назва малими літерами. Чиста функція.
 */
export function standardGroupsOf(fields: readonly IFieldDefinition[], top: IStandardTop): IProtoGroup[] {
  const flds = fields.filter((f) => f.key !== 'title' && !(top.status && f.key === 'status'));
  const keys = (pick: (f: IFieldDefinition) => boolean) => flds.filter(pick).map((f) => f.key);
  const groups: Array<[string, string[]]> = [
    ['Management', [...keys((f) => f.key === 'assignee'), ...keys((f) => f.type === 'user' && f.key !== 'assignee'),
      ...RATED.filter((k) => flds.some((f) => f.key === k)), ...keys((f) => OWN.includes(f.key) && f.type !== 'user' && !RATED.includes(f.key))]],
    ['Links', keys((f) => f.type === 'ref' || f.type === 'lookup')],
    ['Planning', keys((f) => f.type === 'area' || f.type === 'iteration')],
    ['Dates', keys((f) => f.type === 'date')],
    ['Details', keys((f) => !OWN.includes(f.key) && !NOT_DETAILS.includes(f.type) && (f.type !== 'tags' || !top.tags))],
    ['Notes', keys((f) => f.type === 'textarea' && (f.key !== 'description' || !top.description))],
  ];
  return groups.filter(([, list]) => list.length > 0)
    .sort(([a], [b]) => ORDER.indexOf(a) - ORDER.indexOf(b))
    .map(([title, list]) => ({ id: title.toLowerCase(), title: { en: title }, full: title === 'Notes', keys: list }));
}
